import { useEffect, useState } from 'react';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Collaboration from '@tiptap/extension-collaboration';
import * as Y from 'yjs';
import { fromB64 } from '../../../core/crypto';
import type { DocumentPlain } from '../sync';
import { PasswordField } from './PasswordFieldExtension';
import { InlineImage } from './InlineImageExtension';
import { AttachmentChip } from './AttachmentChipExtension';

// Spins up a throwaway TipTap editor bound to a fresh Y.Doc seeded with the
// plain payload's yjsState, extracts the HTML, and regex-redacts password
// values / image blobs / attachment chips so the preview never leaks plaintext
// secrets. Used by VersionHistoryDrawer (snapshot preview) and OrphanageModal
// (deleted-document preview).
export function DocumentPreviewPane({ plain }: { plain: DocumentPlain | null }) {
  const [html, setHtml] = useState('');

  useEffect(() => {
    let cancelled = false;
    let editor: Editor | null = null;
    let ydoc: Y.Doc | null = null;

    if (!plain) {
      setHtml('');
      return;
    }

    void (async () => {
      let out = '';

      if (plain.yjsState) {
        try {
          ydoc = new Y.Doc();
          Y.applyUpdate(ydoc, fromB64(plain.yjsState));
          editor = new Editor({
            extensions: [
              StarterKit.configure({ heading: { levels: [1, 2, 3] }, history: false }),
              Link.configure({ openOnClick: false }),
              TaskList,
              TaskItem.configure({ nested: true }),
              PasswordField,
              InlineImage,
              AttachmentChip,
              Collaboration.configure({ document: ydoc }),
            ],
            editable: false,
          });
          out = editor.getHTML();
        } catch {
          out = '';
        }
      }

      if (out) {
        out = redact(out);
      }

      if (!cancelled) setHtml(out);

      if (editor) {
        try { editor.destroy(); } catch { /* ignore */ }
      }
      if (ydoc) {
        try { ydoc.destroy(); } catch { /* ignore */ }
      }
    })();

    return () => {
      cancelled = true;
      if (editor) {
        try { editor.destroy(); } catch { /* ignore */ }
      }
      if (ydoc) {
        try { ydoc.destroy(); } catch { /* ignore */ }
      }
    };
  }, [plain?.yjsState, plain?.markdown]);

  if (!plain) return null;
  if (!plain.markdown) {
    return <p className="text-sm text-ink-500">(empty)</p>;
  }
  return (
    <div
      className="tiptap prose-invert max-w-none text-sm pointer-events-none select-text"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function redact(html: string): string {
  let out = html.replace(
    /<msp-password\s*([^>]*)>([\s\S]*?)<\/msp-password>/gi,
    (_match, attrs) => {
      const labelMatch = /label\s*=\s*"([^"]*)"/i.exec(attrs as string);
      const label = labelMatch ? decodeHtml(labelMatch[1] ?? '') : 'Password';
      return `<span class="password-field" style="pointer-events:none">🔑 ${escapeHtml(label)} <span class="opacity-60">••••••••</span></span>`;
    },
  );
  out = out.replace(
    /<inline-image\s*([^>]*)><\/inline-image>/gi,
    (_match, attrs) => {
      const altMatch = /alt\s*=\s*"([^"]*)"/i.exec(attrs as string);
      const alt = altMatch ? decodeHtml(altMatch[1] ?? '') : 'image';
      return `<span class="inline-image-placeholder" style="pointer-events:none;display:inline-block;padding:0.25rem 0.5rem;border:1px dashed #475569;border-radius:0.25rem">🖼 ${escapeHtml(alt)}</span>`;
    },
  );
  out = out.replace(
    /<attachment-chip\s*([^>]*)><\/attachment-chip>/gi,
    (_match, attrs) => {
      const nameMatch = /filename\s*=\s*"([^"]*)"/i.exec(attrs as string);
      const name = nameMatch ? decodeHtml(nameMatch[1] ?? '') : 'attachment';
      return `<span class="attachment-chip" style="pointer-events:none">📎 ${escapeHtml(name)}</span>`;
    },
  );
  return out;
}

function decodeHtml(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c] ?? c,
  );
}
